import { createRouter, createWebHistory } from 'vue-router';
import SharePointService from '../services/sharepoint';

// ─── Auth ───────────────────────────────────────────────────────────
import LoginPage from '../LoginPage.vue';

// ─── Admin views ────────────────────────────────────────────────────
import AdminClientList from '../views/AdminClientList.vue';
import ClientDetailPage from '../views/ClientDetailPage.vue';
import AdminAnnouncements from '../views/AdminAnnouncements.vue';
import AdminDocumentVault from '../views/AdminDocumentVault.vue';
import AdminMessages from '../views/AdminMessages.vue';
import AdminTasksWorkflow from '../views/AdminTasksWorkflow.vue';

// ─── Client views ────────────────────────────────────────────────────
import InvitePage from '../views/InvitePage.vue';
import ClientDashboard from '../views/client/ClientDashboard.vue';
import ClientDocuments from '../views/client/ClientDocuments.vue';
import ClientMessages from '../views/client/ClientMessages.vue';
import ClientTasks from '../views/client/ClientTasks.vue';
import ClientAnnouncements from '../views/client/ClientAnnouncements.vue';

// ─── Admin email list (must match LoginPage.vue) ─────────────────────
const routes = [

  // ── Public ──────────────────────────────────────────────────────────
  { path: '/', redirect: '/login' },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage
  },
  {
    path: '/invite',
    name: 'InvitePage',
    component: InvitePage
    // No auth required — token in URL query param handles security
  },

  // ── Admin routes ─────────────────────────────────────────────────────
  {
    path: '/admin/clients',
    name: 'AdminClientList',
    component: AdminClientList,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/clients/:id',
    name: 'ClientDetailPage',
    component: ClientDetailPage,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/announcements',
    name: 'AdminAnnouncements',
    component: AdminAnnouncements,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/documents',
    name: 'AdminDocumentVault',
    component: AdminDocumentVault,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/messages',
    name: 'AdminMessages',
    component: AdminMessages,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/tasks',
    name: 'AdminTasksWorkflow',
    component: AdminTasksWorkflow,
    meta: { requiresAuth: true, role: 'admin' }
  },

  // ── Client routes ─────────────────────────────────────────────────────
  {
    path: '/client/dashboard',
    name: 'ClientDashboard',
    component: ClientDashboard,
    meta: { requiresAuth: true, role: 'client' }
  },
  {
    path: '/client/documents',
    name: 'ClientDocuments',
    component: ClientDocuments,
    meta: { requiresAuth: true, role: 'client' }
  },
  {
    path: '/client/messages',
    name: 'ClientMessages',
    component: ClientMessages,
    meta: { requiresAuth: true, role: 'client' }
  },
  {
    path: '/client/tasks',
    name: 'ClientTasks',
    component: ClientTasks,
    meta: { requiresAuth: true, role: 'client' }
  },
  {
    path: '/client/announcements',
    name: 'ClientAnnouncements',
    component: ClientAnnouncements,
    meta: { requiresAuth: true, role: 'client' }
  },

  // ── 404 fallback ──────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login'
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// ─── Navigation Guard ─────────────────────────────────────────────────
router.beforeEach((to, from, next) => {
  if (!to.meta.requiresAuth) {
    next();
    return;
  }

  const requiredRole = to.meta.role;

  // Helper: check valid client session token
  function getClientSession() {
    try {
      const token = sessionStorage.getItem('clientSession');
      if (!token) return null;
      const [data] = token.split('.');
      if (!data) return null;
      const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp || Date.now() > payload.exp) {
        sessionStorage.removeItem('clientSession');
        return null;
      }
      return payload;
    } catch { return null; }
  }

  // CLIENT routes — accept OTP session
  if (requiredRole === 'client') {
    if (getClientSession()) { next(); return; }
    next('/login');
    return;
  }

  // ADMIN routes — require MSAL
  if (requiredRole === 'admin') {
    try {
      if (SharePointService.isAuthenticated()) {
        next();
        return;
      }
    } catch { /* not authenticated */ }
    next('/login');
    return;
  }

  next();
});

export default router;